// ─────────────────────────────────────────────
//  attendingCallList — the day's MD call order, parsed from EVERY hospital's
//  attending column in the Prelim Anes Assignments sheet (not just Wesley
//  Chapel). Each attending carries a slot: a Post-Call number (PC1–PC6), a call
//  number (1..N), or none ("L" = last / unassigned). Wesley Chapel attendings
//  (supervising a WC*/WOR* room) are flagged `wc` so the UI can colour them red.
//
//  Pure — no React, no Firestore. Input is the raw 2D `values` array.
// ─────────────────────────────────────────────
import { WC_ROOM_RE, parseAttendingCell } from "./cellReader";
import { facilityLabel, shortFacilityLabel } from "../../domain/facilities";

const COL = { room: 0, attending: 4 };
const norm = (v) => String(v ?? "").trim();

// Skip the header row ("OR# Surgeon Procedure Duration Attending AHP").
function isHeaderRow(row) {
  const joined = (row || []).map(norm).join(" ").toLowerCase();
  return /\bprocedure\b/.test(joined) && /(attending|surgeon|ahp)/.test(joined);
}

// Parse the facility-wide attending call list.
// @returns Array<{ code, callOrder, postCall, isAIC, wc }>  (deduped by code)
export function parseAttendingCallList(values) {
  const all = Array.isArray(values) ? values : [];
  const byCode = new Map();

  for (const raw of all) {
    const row = raw || [];
    if (isHeaderRow(row)) continue;
    const room = norm(row[COL.room]);
    if (!room) continue;
    const att = parseAttendingCell(row[COL.attending]);
    // "PCdoc?" is a placeholder ("post-call doctor — TBD"), not a real attending.
    if (!att.code || att.code === "PCDOC") continue;
    const wc = WC_ROOM_RE.test(room);

    const prev = byCode.get(att.code);
    if (!prev) {
      byCode.set(att.code, {
        code: att.code,
        callOrder: att.callOrder ?? null,
        postCall: att.postCall ?? null,
        isAIC: Boolean(att.isAIC),
        wc,
      });
    } else {
      // Same attending on multiple rooms: keep the first defined slot, OR-in the
      // WC flag and AIC flag (red if they cover ANY WC room).
      byCode.set(att.code, {
        code: att.code,
        callOrder: prev.callOrder ?? att.callOrder ?? null,
        postCall: prev.postCall ?? att.postCall ?? null,
        isAIC: prev.isAIC || Boolean(att.isAIC),
        wc: prev.wc || wc,
      });
    }
  }

  return [...byCode.values()];
}

// ── Location/role colour coding for MD Call names ──
// The provider LEADING each campus today is colour-coded wherever the MD Call
// list renders (board + TV): Wesley Chapel AIC red, Tampa DOD blue,
// Carrollwood AIC green. Identity comes from each facility's daily board
// (aicName), matched by normalized name (falls back to the short code).
//
// MULTI-TENANT — WHY THIS IS NO LONGER A LITERAL 3-ID LOOP:
// `aicByFacility` / `doctorsByFacility` are ALREADY keyed by the tenant's own
// live facility ids (useAicByFacility derives them through activeFacilityIds).
// This module used to iterate a hardcoded ["default","tampa","carrollwood"]
// and therefore DISCARDED every key outside those three. The producer had been
// generalized and the consumer had not, so a tenant whose campuses are
// mercy-main/mercy-north received a perfectly correct map and silently
// rendered no colour coding whatsoever — nothing broke, a feature just
// vanished. The campus list now comes from the data itself.
//
// The three built-ins below are the SEED for this deployment, not a source of
// truth: staff here have learned red/blue/green, so those ids keep their exact
// colours and labels, and a tenant overrides per campus on its own facility
// doc (facility.mdCallColor, facility.leadTitle).
export const MD_CALL_LOCATION_COLORS = {
  default: "#dc2626",     // Wesley Chapel AIC — red
  tampa: "#2563eb",       // Tampa DOD — blue
  carrollwood: "#16a34a", // Carrollwood AIC — green
};
export const MD_CALL_LOCATION_LABELS = {
  default: "Wesley Chapel AIC",
  tampa: "Tampa DOD",
  carrollwood: "Carrollwood AIC",
};

// What a campus calls the doctor leading it that day. EDITORIAL per campus,
// exactly like shortTag and for the same reason: Tampa's lead is the "DOD" and
// the others' the "AIC", and no rule derives that from a campus name. Read
// from the facility doc first, then this seed, then the common default.
const BUILT_IN_LEAD_TITLES = { tampa: "DOD" };
const DEFAULT_LEAD_TITLE = "AIC";

// Badge colours for a campus with no configured colour and no built-in seed —
// i.e. every campus belonging to any other tenant. Returning null instead
// would reproduce the very defect this replaced (the colour coding silently
// not rendering), so an unconfigured campus still gets a stable, distinct
// colour and the tenant can override it whenever they care. Chosen to stay
// distinguishable from each other and from the three seeded colours.
const FALLBACK_PALETTE = [
  "#7c3aed", // violet
  "#0891b2", // cyan
  "#c2410c", // burnt orange
  "#4d7c0f", // olive
  "#be123c", // rose
  "#1e40af", // indigo
];

// Stable index for a facility id. Keyed by the id's own characters, NOT by its
// position in the facilities array: an index-based palette would recolour every
// campus the moment a new one is added, so a facility's colour must depend on
// nothing but itself.
function paletteIndex(id, n) {
  const s = String(id);
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % n;
}

const liveFacility = (fid, facilities) =>
  (Array.isArray(facilities) ? facilities.find((f) => f && f.id === fid) : null) || null;

// The colour for a campus: the tenant's own configured value, then the built-in
// seed, then a deterministic palette entry.
export function mdCallCampusColor(fid, facilities = []) {
  const live = liveFacility(fid, facilities);
  if (live?.mdCallColor) return live.mdCallColor;
  if (MD_CALL_LOCATION_COLORS[fid]) return MD_CALL_LOCATION_COLORS[fid];
  return FALLBACK_PALETTE[paletteIndex(fid, FALLBACK_PALETTE.length)];
}

// The label for a campus's leader — "<campus> <lead title>", e.g.
// "Wesley Chapel AIC" / "Tampa DOD". DERIVED from the tenant's own facility
// names (org prefix stripped the same way the topbar tag strips it) rather
// than stored as three fixed strings. With no live docs it returns the built-in
// label verbatim, so this deployment reads identically either way.
export function mdCallCampusLabel(fid, facilities = []) {
  const live = liveFacility(fid, facilities);
  const title = live?.leadTitle || BUILT_IN_LEAD_TITLES[fid] || DEFAULT_LEAD_TITLE;
  const labels = (Array.isArray(facilities) ? facilities : [])
    .map((f) => f && (f.name || f.label))
    .filter(Boolean);
  if (!labels.length) return MD_CALL_LOCATION_LABELS[fid] || `${fid} ${title}`;
  const campus = shortFacilityLabel(facilityLabel(fid, facilities), labels);
  return `${campus} ${title}`.trim();
}

// The campuses to consider, in a DETERMINISTIC order, taken from the maps the
// caller actually holds. Object key order cannot be trusted here:
// useAicByFacility fills these from async onSnapshot callbacks, so insertion
// order depends on which board's snapshot lands first and varies between page
// loads — an unsorted iteration would give a doctor who leads two campuses a
// badge colour that changes on refresh. Built-in campuses lead, in their
// original order, so this deployment's precedence is byte-identical to the
// hardcoded loop this replaced; every other campus follows, sorted by id.
function campusOrder(...maps) {
  const seen = new Set();
  for (const m of maps) {
    for (const k of Object.keys(m || {})) if (k) seen.add(k);
  }
  const builtIn = Object.keys(MD_CALL_LOCATION_LABELS).filter((id) => seen.has(id));
  const rest = [...seen].filter((id) => !(id in MD_CALL_LOCATION_LABELS)).sort();
  return [...builtIn, ...rest];
}

const nameKey = (n) => String(n || "").toLowerCase().replace(/[^a-z]/g, "");

// The shortest fragment allowed to match by CONTAINMENT. Full names differ in
// form between the sheet and the roster ("Dr. Osunsanmi" vs "Babajide (Michael)
// Osunsanmi"), so containment is genuinely needed — but only between things long
// enough to be a name.
const MIN_CONTAINS = 5;

// ⚠️ A SHORT CODE MAY ONLY MATCH EXACTLY. NEVER BY CONTAINMENT.
// This used to test every candidate — including the doctor's two-letter short
// code — with `aic.includes(c) || c.includes(aic)`. A code of "AS" (Antonio
// Santos) is a substring of any AIC whose name contains those letters
// ("jasonjohnson", "thomas"), so the rail labelled a doctor who is NOT the AIC
// as "Wesley Chapel AIC" — reported live 2026-08-17, with two people wearing the
// same badge, which is impossible: there is one AIC per campus.
//
// A wrong AIC badge is not cosmetic. The AIC is who you ring when a room is in
// trouble, so a false one sends that call to somebody who cannot answer it.
//
// `name` may still match by containment (the two sources really do disagree on
// form), but only when both sides are long enough to mean something.
function personMatches({ name, code }, target) {
  const t = nameKey(target);
  if (!t) return false;
  const n = nameKey(name);
  const c = nameKey(code);
  if (c && c === t) return true;
  if (!n) return false;
  if (n === t) return true;
  if (n.length < MIN_CONTAINS || t.length < MIN_CONTAINS) return false;
  return t.includes(n) || n.includes(t);
}

// entry: { name?, code? } · aicByFacility: { <facilityId>: aicName } ·
// facilities: the tenant's live facility docs (optional — omitting them keeps
// the built-in seed's colours and labels).
// Returns { color, label, facilityId } or null when the person leads no campus
// today. `facilityId` is returned so callers never have to map a display label
// back to an id.
export function mdCallLocationRole(entry, aicByFacility = {}, facilities = []) {
  const who = { name: entry?.name, code: entry?.code };
  if (!nameKey(who.name) && !nameKey(who.code)) return null;
  for (const fid of campusOrder(aicByFacility)) {
    if (!aicByFacility[fid]) continue;
    if (personMatches(who, aicByFacility[fid])) {
      return {
        color: mdCallCampusColor(fid, facilities),
        label: mdCallCampusLabel(fid, facilities),
        facilityId: fid,
      };
    }
  }
  return null;
}

// The NUMBER BADGE colour for an MD Call entry: tinted by the campus the
// doctor is WORKING at today (red Wesley · blue Tampa · green Carrollwood).
// Names stay black — only the badge carries the location. Identity comes from
// each facility's daily-board supervisors list (+ its AIC). Unknown → null
// (caller keeps its default badge colour).
export function mdCallBadgeColor(
  entry,
  { aicByFacility = {}, doctorsByFacility = {}, facilities = [] } = {}
) {
  const who = { name: entry?.name, code: entry?.code };
  if (!nameKey(who.name) && !nameKey(who.code)) return null;
  // Same rule as the AIC role above, and for the same reason: a two-letter code
  // substring-matched into a name tints the badge for the wrong campus.
  const matches = (target) => personMatches(who, target);
  for (const fid of campusOrder(aicByFacility, doctorsByFacility)) {
    if (matches(aicByFacility[fid])) return mdCallCampusColor(fid, facilities);
    if ((doctorsByFacility[fid] || []).some(matches)) return mdCallCampusColor(fid, facilities);
  }
  return null;
}

// Badge token shown in the circle: "PC3" | "8" | "L".
export function mdCallBadge(e) {
  if (!e) return "L";
  if (e.postCall != null) return `PC${e.postCall}`;
  if (e.callOrder != null) return String(e.callOrder);
  return "L";
}

// Sort (TV MD-call order, per spec):
//   1) Post-Call slots first, ASCENDING (PC1, PC2, … PC6)
//   2) then on-call doctors DESCENDING (13, 12, … 1 — 1 is primary on-call, so
//      it sits closest to the top of the group / just above the tail)
//   3) then "L" (no slot) by name
//   4) then locums at the very bottom (isLocum overrides any slot)
export function sortMdCall(list) {
  const rank = (e) =>
    e.isLocum ? 3 : e.postCall != null ? 0 : e.callOrder != null ? 1 : 2;
  return [...(list || [])].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    if (ra === 0) return a.postCall - b.postCall; // PC ascending
    if (ra === 1) return b.callOrder - a.callOrder; // on-call descending (1 last)
    // L and locum groups: stable, by name
    return String(a.name || a.code || "").localeCompare(String(b.name || b.code || ""));
  });
}
